import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// --- Components & Styles ---
import '../Styles/ManageOutstandings.css';
const API_URL = process.env.REACT_APP_API_URL;
// --- Helper Functions ---
const getStatusBadge = (status, dueDate) => {
  const isOverdue = dueDate ? new Date(dueDate) < new Date() : false;
  const currentStatus = (status || 'UNPAID').toUpperCase();

  switch (currentStatus) {
    case 'PAID':
      return <span className="status-badge paid">Paid</span>;
    case 'UNPAID':
      return (
        <span className={`status-badge unpaid ${isOverdue ? 'overdue' : ''}`}>
          {isOverdue ? 'Overdue' : 'Unpaid'}
        </span>
      );
    default:
      return <span className="status-badge other">{status}</span>;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB');
};

// --- Main Component ---
const ManageOutstandings = () => {
  const [customerGroups, setCustomerGroups] = useState([]); // State now holds a simple array
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const abortRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ✅ MODIFIED: Simplified fetch function
  const fetchOutstandings = useCallback(async (searchTerm = '') => {
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);

    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    try {
      // ✅ Use the new, dedicated endpoint
      const url = `${API_URL}/reports/outstandings-by-customer?search=${encodeURIComponent(searchTerm)}`;
      const res = await axios.get(url, {
        headers,
        signal: abortRef.current.signal,
      });

      // ✅ The backend now sends data in the perfect format. No more client-side grouping needed!
      setCustomerGroups(res.data || []);

    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error('Failed to fetch outstandings:', err);
      toast.error('Could not load outstanding invoices.');
    } finally {
      setLoading(false);
    }
  }, [headers, navigate, token]);

  // ✅ MODIFIED: Debounced search effect
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOutstandings(search);
    }, 300); // 300ms delay

    return () => clearTimeout(handler);
  }, [search, fetchOutstandings]);


  const handleDownloadStatement = async (customerCode, customerName) => {
    if (!customerCode) {
      toast.error('Cannot generate statement for a customer without a code.');
      return;
    }
    setDownloading(String(customerCode));
    try {
      // ✅ CORRECTED ENDPOINT: Now points to your new payments route.
      const response = await axios.get(
        `${API_URL}/payments/reports/statement/${customerCode}`,
        {
          headers,
          responseType: 'blob', // Important for file downloads
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (customerName || 'customer').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      
      // ✅ CORRECTED FILE EXTENSION: The file is now a PDF.
      link.setAttribute('download', `statement_${safeName}.pdf`); 
      
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download statement:', error);
      // Check for specific error details from the backend if available
      const errorMsg = error.response?.data?.detail || 'There was an error generating the PDF statement.';
      toast.error(errorMsg);
    } finally {
      setDownloading('');
    }
};

  // ✅ No more useMemo for filtering needed, as the backend handles it.
  const filteredGroups = customerGroups;

  return (
    <>
      <div className="manage-container">
        <div className="toolbar">
          <input
            className="search-bar"
            type="text"
            placeholder="Search by customer name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading-state full-page">Loading...</div>
        ) : filteredGroups.length > 0 ? (
          <div className="customer-grid">
            {filteredGroups.map((data) => (
              <div
                className="customer-card"
                key={`${data.customercode}|${data.customerName}`}
              >
                <div className="customer-header">
                  <h3>{data.customerName}</h3>
                  <div>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleDownloadStatement(data.customercode, data.customerName)}
                      disabled={!data.customercode || (downloading && downloading === String(data.customercode))}
                    >
                      {downloading && downloading === String(data.customercode) ? 'Generating...' : 'Download Statement'}
                    </button>
                  </div>
                </div>

                <div className="customer-total">
                  Total Due: <strong>₹{data.totalDue.toFixed(2)}</strong>
                </div>

                <div className="table-wrapper">
                  <table className="data-table">
                     <thead>
                      <tr>
                        <th className="docno-cell">Invoice #</th>
                        <th className="date-cell">Date</th>
                        <th className="date-cell">Due Date</th>
                        <th className="amount-cell">Amount Due</th>
                        <th className="status-cell">Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map((inv) => (
                        <tr key={`${inv.docno}-${inv.finyear}`}>
                          <td data-label="Invoice #" className="docno-cell"><span>{inv.docno}</span></td>
                          <td data-label="Invoice Date" className="date-cell"><span>{formatDate(inv.docdate)}</span></td>
                          <td data-label="Due Date" className="date-cell"><span>{formatDate(inv.due_date)}</span></td>
                          <td data-label="Amount Due" className="amount-cell">
                            <span>₹{parseFloat(inv.due_amount || 0).toFixed(2)}</span>
                          </td>
                          <td data-label="Status" className="status-cell">
                            {getStatusBadge(inv.payment_status, inv.due_date)}
                          </td>
                          <td data-label="Actions" className="actions-cell">
                            <button
                              className="btn btn-outline"
                              onClick={() =>
                                navigate(
                                  `/billing/view/${inv.docno}/${inv.finyear}?doctype=${inv.doctype}` // Corrected path
                                )
                              }
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="message-box">
            {search ? `No matches for “${search}”.` : 'No outstanding invoices found.'}
          </div>
        )}
      </div>
    </>
  );
};

export default ManageOutstandings;